/**
 * SOFT_DELETE_STANDARD (F4-A) — restore from trash.
 *
 * Goes through PowerSync's normal write-path (the server branch validates
 * access and records `restored` in activity). Space's trash is
 * RESTORE-ONLY on purpose (the guide's field-check variant): the write-path
 * turns the client's DELETE into a server-side soft-delete, and the physical
 * deletion is done ONLY by the purge job (`TRASH_PURGE_DAYS`, default 30 days),
 * leaving a `purged` entry with a snapshot in the log.
 */
import {
  assignment,
  course_module,
  eq,
  lesson,
  type PowerSyncDrizzleDB,
  resource,
} from "@space/powersync";
import { nowISO } from "../utils/date-helpers";
import {
  patchRowVerified,
  type VerifiedWriteOptions,
  waitForRowPresent,
  withScope,
} from "../powersync/verified-write";
import { TRASH_ORG_COLUMN, TRASH_TABLES, type TrashKind } from "./queries";

/**
 * `organizationId`: the caller's ACTIVE org — see the `scope` note in
 * `verified-write.ts`. `TRASH_ORG_COLUMN[kind]` is `undefined` for the one
 * kind without that column (`course_module`, see `queries.ts`); `withScope`
 * discards an empty scope, so that kind stays exactly as it was.
 */
function orgScope(kind: TrashKind, organizationId: string | undefined) {
  const column = TRASH_ORG_COLUMN[kind];
  return column ? [{ column, value: organizationId }] : [];
}

/** Restores a row from trash (deleted_at → NULL). */
export async function restoreTrashedMutation(
  db: PowerSyncDrizzleDB,
  kind: TrashKind,
  id: string,
  organizationId: string | undefined,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const table = TRASH_TABLES[kind];
  await patchRowVerified(
    db,
    table,
    table.id,
    id,
    { deleted_at: null },
    withScope(options, orgScope(kind, organizationId)),
  );
}

/** Sends a live row back to trash (reverse of restore, for L1 undo). */
export async function trashAgainMutation(
  db: PowerSyncDrizzleDB,
  kind: TrashKind,
  id: string,
  organizationId: string | undefined,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const table = TRASH_TABLES[kind];
  await patchRowVerified(
    db,
    table,
    table.id,
    id,
    { deleted_at: nowISO() },
    withScope(options, orgScope(kind, organizationId)),
  );
}

/** Soft-deletable ref of a course module (deletion/restoration in pairs). */
function moduleRefTable(
  kind: string | null,
): typeof lesson | typeof resource | typeof assignment | null {
  switch (kind) {
    case "lesson": {
      return lesson;
    }
    case "resource": {
      return resource;
    }
    case "assignment": {
      return assignment;
    }
    default: {
      return null;
    }
  }
}

/**
 * The referenced rows that a course module's soft delete and restore move
 * together with it: one entry when the module's kind has a ref table and the
 * module points at a row, none otherwise. Pure, so the cascade decision can be
 * tested without a database; the writes stay in the mutations below.
 */
export function courseModuleCascadeRefs(
  mod: { kind: string | null; ref_id: string | null } | undefined,
): { table: NonNullable<ReturnType<typeof moduleRefTable>>; id: string }[] {
  const table = moduleRefTable(mod?.kind ?? null);
  return table && mod?.ref_id ? [{ table, id: mod.ref_id }] : [];
}

/**
 * ⚠️ No `scope` on purpose for the REFERENCED row (lesson/resource/
 * assignment), even though `resource`/`assignment` DO have `organization_id`.
 * 2026-08-13 audit (see twin notes in `academy/modules/mutations.ts` and
 * `academy/assessment/mutations.ts`): that content's real authorization is
 * COURSE author/editor (`db/sync.rs`, CC-40), not organization — a course is
 * authored independently of organization and shared across academies via
 * cohorts. `organization_id` stays NULL along the authoring path (confirmed
 * by reading the `insert`s), so a `scope` by active org would protect
 * nothing (the real boundary is the user) and would also break every
 * cascade restore/re-delete. `course_module` itself doesn't have that
 * column either — it inherits the course's isolation.
 *
 * Restores a `course_module` TOGETHER WITH its referenced row (lesson/resource/
 * assignment): authoring's deletion flow soft-deletes both — restoring
 * only the module would leave the content orphaned-hidden.
 */
export async function restoreCourseModuleCascadeMutation(
  db: PowerSyncDrizzleDB,
  moduleId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  // The module is a row that ALREADY EXISTED (it's being restored from trash):
  // unlike a creation, waiting here doesn't penalize any "genuinely
  // new" path — if the SELECT lands in the window right after the module was
  // created some other way, `waitForRowPresent` avoids reading an empty `mod` and skipping
  // the cascade to the referenced lesson/resource/assignment.
  await waitForRowPresent(db, course_module, course_module.id, moduleId, options);
  const rows = await db.select().from(course_module).where(eq(course_module.id, moduleId)).limit(1);
  await patchRowVerified(
    db,
    course_module,
    course_module.id,
    moduleId,
    { deleted_at: null },
    options,
  );
  for (const ref of courseModuleCascadeRefs(rows[0])) {
    await patchRowVerified(db, ref.table, ref.table.id, ref.id, { deleted_at: null }, options);
  }
}

/** Reverse of the cascade restore (L1 undo): re-deletes module + ref. No `scope` — see the note on `restoreCourseModuleCascadeMutation`. */
export async function trashCourseModuleCascadeMutation(
  db: PowerSyncDrizzleDB,
  moduleId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await waitForRowPresent(db, course_module, course_module.id, moduleId, options);
  const rows = await db.select().from(course_module).where(eq(course_module.id, moduleId)).limit(1);
  const now = nowISO();
  await patchRowVerified(
    db,
    course_module,
    course_module.id,
    moduleId,
    { deleted_at: now },
    options,
  );
  for (const ref of courseModuleCascadeRefs(rows[0])) {
    await patchRowVerified(db, ref.table, ref.table.id, ref.id, { deleted_at: now }, options);
  }
}
